// A widget whose connection signed out (a remote MCP server's sign-in ran out): Sign in, right on its tile, in one
// click. The sign-in window opens on that click; while the user signs in the button says so and shows the window
// again, a pop-up blocker turns it into the button that opens it, and once signed in the widget reads again.

import { useEffect } from "react";
import { LogIn } from "lucide-react";
import type { Connection, Stream } from "../../shared/types";
import { cx } from "../lib/util";
import { signInAgain } from "../state/connections";
import { toast, useStore } from "../state/store";
import { useSignIn } from "../panels/connect/SignIn";

/** The connection a stream's source reads through, when it needs the user to sign in again. */
export function useSignedOut(stream: Stream | null | undefined): Connection | null {
  return useStore((s) => {
    const p = stream?.pullId ? s.pulls[stream.pullId] : undefined;
    const c = p?.config.connection ? s.connections[p.config.connection] : undefined;
    return c && c.auth === "oauth" && (c.status === "needs_auth" || c.status === "error") ? c : null;
  });
}

/** small: in the tile's header, beside the last good value; else the button of a tile with nothing to show yet. */
export function SignInAgain({ conn, small }: { conn: Connection; small?: boolean }) {
  const si = useSignIn();
  const waiting = !!si.wait && !si.wait.blocked;
  const label = si.wait?.blocked ? "Open the sign-in" : waiting ? (si.closed ? "Open the sign-in" : "Signing in") : small ? "Sign in" : `Sign in to ${conn.name}`;
  // a sign-in that failed (not one blocked or closed, which wait here) says why, since the tile has no room to
  useEffect(() => {
    if (si.error && !si.wait) toast({ level: "error", title: `${conn.name} is not signed in`, body: si.error });
  }, [si.error]); // eslint-disable-line react-hooks/exhaustive-deps
  const start = () => void si.run((opts) => signInAgain(conn.id, opts));
  return (
    <button
      className={cx("btn", small ? "btn-xs tile-signin" : "btn-sm btn-primary", si.wait?.blocked && small && "btn-primary")}
      title={waiting ? `Waiting for you to sign in to ${conn.name}. Click to show the window.` : `${conn.name} signed this board out. Sign in again and this widget reads it again.`}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        if (si.wait) si.reopen();
        else start();
      }}
      data-interactive
    >
      {waiting && !si.closed ? <span className="spinner" /> : <LogIn size={small ? 11 : 13} />} {label}
    </button>
  );
}
